import React, { useCallback, useContext, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import firebaseAuth from "../../services/firebase/firebaseAuth";
import SplitForm from "../../components/SplitForm";
import { useState } from "react";
import BasicInfoForm from "./BasicInfoForm";
import ValidateCodeForm from "./ValidateCodeForm";
import { connect, useDispatch } from "react-redux";
import { updateUser } from "../../store/reducers/currentUserSlice";
import axios from "../../services/axios/backendAxios";
import { useMutation, useQuery } from "@tanstack/react-query";
import errors from "../../services/errors";
import { AuthContext } from "../../AuthContext";
import SnowScapeLogoWithWhiteText from "../../resources/logo/snowscape-logo-horizontal-with-white-text.svg";

import { Pricing } from "@mysnowscape/snow-lib";
const AddOnPricing = Pricing.AddOnPricing;

// Load Facebook Meta Pixel once per browser session and fire a single PageView
function trackFacebookPixel() {
  if (typeof window === "undefined") return;
  try {
    if (window.fbq) {
      // window.fbq("init", "684600627650712");
      window.fbq("track", "PageView");
      return;
    }

    (function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(
      window,
      document,
      "script",
      "https://connect.facebook.net/en_US/fbevents.js",
    );

    // init and track immediately — fbq is defined by the snippet and queues calls
    window.fbq("init", "684600627650712");
    window.fbq("track", "PageView");
  } catch (err) {
    // swallow errors silently — analytics shouldn't break the page
    // eslint-disable-next-line no-console
    console.error("FB Pixel load error:", err);
  }
}
const SignUpPage = ({ currentUser }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { authUser: firebaseUser } = useContext(AuthContext);

  // Get the desired plan from the url if provided:
  const [searchParams] = useSearchParams();
  let selectedPlan = searchParams.get("plan");

  const [sentCode, setSentCode] = useState(false); // have we sent the customer a SMS
  const [formData, setFormData] = useState(null); // The result of the BasicInfo form
  const [formError, setFormError] = useState(null);

  const { data: pricingData, isLoading } = useQuery({
    queryKey: ["stripe/pricing"],
  });
  const plans = pricingData?.plans ?? [];

  const signUpForPlanWithSubscription = useMutation({
    mutationFn: ({
      plan,
      extra_users,
      extra_companies,
      email,
      companyName,
    }) => {
      return axios.post("/stripe/create-customer-with-subscription", {
        plan,
        email,
        addons: {
          extra_users,
          extra_companies,
        },
        companyName,
      });
    },
    retry: 3,
  });

  useEffect(() => {
    firebaseAuth.getRecaptchaVerify();
  }, []);

  useEffect(() => {
    trackFacebookPixel();
  }, []);

  // Only visible on Desktop - so make it helpful but not critical:
  let leftContent = sentCode ? (
    <p className="text-xl">
      Almost there - check your phone for the code we sent you.
    </p>
  ) : (
    <p className="text-xl">
      Welcome to SnowScape - the solution for landscape business owners! We're
      excited to have you join us. To get started, simply fill in some basic
      information about your company.
    </p>
  );

  const requestCodeFromFirebase = useCallback(async (data) => {
    try {
      const phoneNumber = data.phone;
      const cleanedNumber = "+1" + ("" + phoneNumber).replace(/\D/g, "");
      await firebaseAuth.sendSMSAsync(cleanedNumber);
      setSentCode(true);
    } catch (err) {
      errors.report(err);
      setFormError(
        "Unable to send code - please check your phone number and try again.",
      );
    }
  }, []);

  const resendCode = useCallback(async () => {
    try {
      await firebaseAuth.sendSMSAsync(cleanPhoneNumber(formData.phone));
    } catch (err) {
      errors.report(err);
      setFormError(
        "Unable to send code - please check your phone number and try again.",
      );
    }
  }, [formData]);

  const openStripeCheckoutPage = useCallback(
    async (plan, extra_users, extra_companies, email) => {
      setFormError(null);
      try {
        await goToStripe(plan, extra_users, extra_companies, email);
      } catch (error) {
        setFormError(error);
      }
    },
    [],
  );

  const setupUserAndCompany = useCallback(
    async (fbUser, formData) => {
      try {
        // Create a new user and send it to the backend:
        const newUser = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: fbUser.phone_number,
          startDate: new Date().toLocaleDateString(),
          uid: fbUser.user_id,
          agreeToTerms: formData.agreeToTerms,
          email: formData.email ?? undefined,
        };
        const response = await axios.post("/user/create-with-account", newUser);
        dispatch(updateUser(response.data.user));
        // It's a normal flow that the user might already exist, but went through sign-up anyway:
        if (
          response.data.code === "user-already-exists" &&
          response.data.account.stripe?.customerId != null &&
          response.data.account.licensedCompanies?.length > 0
        ) {
          alert(
            "You already have an account. We will log you in now.  If you want to change your plan, go to Settings > Billing.",
          );
          navigate("/admin/settings/billing");
          return;
        }

        // If we were passed an existing company - make it the current company:
        if (formData.existingCompanyId) {
          dispatch(
            updateUser({ currentCompanyId: formData.existingCompanyId }),
          );
        }

        // And create a company if the form data has a company name
        let companyDataPromise = null;
        if (formData.companyName) {
          companyDataPromise = axios
            .post("company/add", {
              name: formData.companyName,
              uid: fbUser.uid,
            })
            .then((resp) => {
              dispatch(
                // This line makes sure they have a company set when they login and updates our cached user's companies
                updateUser({
                  ...resp.data.user,
                  currentCompanyId: resp.data.company._id,
                }),
              );
            });
        }

        // Now lookup the plan and handle the subscription:
        let planData = plans.find((i) => i.id == formData.plan.id);
        if (!planData) {
          // Normally should not be able to get here (unless we have a bug or the user is tweaking the url)
          alert(
            "Sorry - we couldn't find the plan you selected - please try again",
          );
          navigate("/sign-up");
          return;
        }
        if (
          planData.price <= 0 &&
          formData.extra_users < 1 &&
          formData.extra_companies < 1
        ) {
          // Create stipe customer without collecting payment data
          await signUpForPlanWithSubscription.mutateAsync({
            plan: formData.plan.id,
            email: formData.email,
            companyName: formData.companyName,
          });
          await companyDataPromise; // make sure this has finished before we move on
          navigate("/admin/dashboard?show_purchase_success=1");
          return;
        } else {
          await companyDataPromise; // make sure this has finished before we move on
          await openStripeCheckoutPage(
            formData.plan.id,
            formData.extra_users,
            formData.extra_companies,
            formData.email,
          );
          return;
        }
      } catch (err) {
        errors.report(err);
        setFormError(
          err.response?.data?.message ??
            err.message ??
            "Please contact support",
        );
      }
    },
    [
      dispatch,
      navigate,
      signUpForPlanWithSubscription,
      openStripeCheckoutPage,
      plans,
    ],
  );

  const onBasicInfoSubmit = useCallback(
    async (data) => {
      setFormData(data);
      if (firebaseUser?.user_id) {
        await setupUserAndCompany(firebaseUser, data); // we are already logged in
      } else {
        console.log("not logged in", firebaseUser);
        await requestCodeFromFirebase(data); // not logged in yet - need to collect code
      }
    },
    [firebaseUser, requestCodeFromFirebase, setupUserAndCompany],
  );

  const validateCodeAndCheckout = useCallback(
    async (verificationCode) => {
      let fbUser = null;
      try {
        // Send code to Firebase
        fbUser = await firebaseAuth.signInWithCodeAsync(verificationCode);
      } catch (error) {
        const errorId = await errors.report(error, {
          action: "SignUpPage:validateCodeAndCheckout",
          formData,
          verificationCode,
        });
        if (error.code === "auth/invalid-verification-code") {
          setFormError("Invalid Code - Please try again. " + errorId);
        } else if (
          error.code === "auth/code-expired" ||
          error.code === "auth/session-expired"
        ) {
          setFormError(
            'Your code has expired.  Please click "Resend Code" above and try again. ' +
              errorId,
          );
          navigate(
            "/login/v2?phoneNumber=" + encodeURIComponent(formData.phone),
          );
        } else {
          setFormError(error);
        }
      }
      if (fbUser) {
        // if the previous step had an error, we might not have a user
        await setupUserAndCompany(
          { ...fbUser, phone_number: fbUser.phoneNumber, uid: fbUser.uid },
          formData,
        );
      }
    },
    [formData, setupUserAndCompany],
  );

  return (
    <>
      <SplitForm leftContent={leftContent}>
        <div className="grid h-full">
          <div className="text-full-white place-self-center">
            <div className="lg:hidden py-4 px-2">
              <img
                alt="SnowScape"
                className="my-auto"
                src={SnowScapeLogoWithWhiteText}
              />
            </div>
            {sentCode ? (
              <ValidateCodeForm
                phone={formData.phone}
                resendCode={resendCode}
                handleSubmit={validateCodeAndCheckout}
              />
            ) : (
              <>
                <div className="px-2">
                  <h3 className="text-2xl">
                    Landscape Contractors: Finally, a free software that
                    instantly solves:
                  </h3>
                  <ul>
                    <li className="list-disc list-inside">
                      Customer Billing Disputes with automated reports
                    </li>
                    <li className="list-disc list-inside">
                      Long billing processes in just 2 clicks
                    </li>
                    <li className="list-disc list-inside">
                      Extra drive time from non-optimized maps and routes
                    </li>
                  </ul>
                  <p className="mt-2">
                    And so much more! Trusted by 1,200+ landscape contractors.
                    Fast & easy to use - setup in less than 30 minutes.
                  </p>
                </div>
                <BasicInfoForm
                  handleSubmit={onBasicInfoSubmit}
                  selectedPlan={selectedPlan}
                  currentUser={currentUser}
                />
                <div className="px-2">
                  <p className="mt-2">
                    You'll be able to set up in less than 30 minutes...
                  </p>
                  <p className="mt-2">It's incredibly easy to use.</p>
                  <p className="mt-2">So that you can:</p>
                  <ul>
                    <li className="list-disc list-inside">
                      Automate billing reports for clients
                    </li>
                    <li className="list-disc list-inside">
                      Manage your entire crew so no time gets wasted
                    </li>
                    <li className="list-disc list-inside">
                      Have satellite, inch-accurate maps so that you know
                      exactly where the work needs to be done.
                    </li>
                    <li className="list-disc list-inside">
                      Avoid billing disputes with our Job Tracker&trade;
                    </li>
                  </ul>
                </div>
              </>
            )}
            {formError ? (
              <div
                className="text-snow-red p-2"
                data-testid="sign-up-page-error-message"
              >
                {formError.message ?? formError}
              </div>
            ) : null}
          </div>
        </div>
      </SplitForm>
    </>
  );
};

async function goToStripe(plan, extra_users, extra_companies, email) {
  let products = [
    {
      lookup_key: plan,
      quantity: 1,
      type: "subscription",
    },
  ];
  if (extra_users > 0) {
    products.push({
      lookup_key: AddOnPricing.extra_users.id,
      quantity: extra_users,
      type: "subscription",
    });
  }
  if (extra_companies > 0) {
    products.push({
      lookup_key: AddOnPricing.extra_companies.id,
      quantity: extra_companies,
      type: "subscription",
    });
  }
  const sessionData = await axios.post("/stripe/create-checkout-session", {
    products: products,
    email: email,
  });
  window.location.href = sessionData.data.sessionUrl;
}

export { goToStripe, SignUpPage };
export default connect((state) => ({ currentUser: state.currentUser }))(
  SignUpPage,
);

export function cleanPhoneNumber(phoneNumber) {
  const cleanedNumber = "+1" + ("" + phoneNumber).replace(/\D/g, "");
  return cleanedNumber;
}
